/**
 * @Module: Ingredient Price Types
 * @Status: Ready
 * @Summary: Data types for the ingredient pricing and cost tracking system
 * @Description: Defines the IngredientPrice registry entry (what the user paid
 *   for a store purchase), IngredientCost (calculated cost for one recipe line),
 *   and RecipeCostSummary (aggregate cost for a full recipe). BaseUnit normalizes
 *   all measurements to grams, milliliters, or whole-count for cost-per-unit math.
 * @NextSteps: Add price history tracking, community price sharing
 */

// removed '../components/icons/AppIcon';

// --- Base types ---

export type BaseUnit = 'g' | 'ml' | 'whole';
export type PriceSource = 'manual' | 'community' | 'api';

// --- Registry entry: what the user paid at the store ---

export interface IngredientPrice {
  id: string;
  name: string;              // normalized lowercase
  displayName: string;       // original casing
  price: number;             // what user paid
  quantity: number;          // how much they got
  unit: string;              // unit of quantity (kg, lb, bag, etc.)
  costPerBaseUnit: number;   // calculated: price / quantity-in-base-units
  baseUnit: BaseUnit;
  category: string;          // produce, dairy, meat, spice, pantry, other
  source: PriceSource;
  recordedAt: string;        // ISO-8601
  updatedAt: string;
  schemaVersion: number;
}

// --- Calculated cost for one ingredient line in a recipe ---

export interface IngredientCost {
  name: string;
  amount: number;
  unit: string;
  cost: number | null;           // null if no price in registry
  costPerBaseUnit: number | null;
}

// --- Aggregate cost for a full recipe ---

export interface RecipeCostSummary {
  totalCost: number;
  costPerServing: number;
  ingredientCosts: IngredientCost[];
  missingPrices: string[];
  coverage: number;              // 0-1, fraction of ingredients with known prices
  allEstimated: boolean;         // true when every priced ingredient uses source='api' (USDA)
}

// --- Constants ---

export const PRICE_SCHEMA_VERSION = 1;

// --- Factory ---

export function emptyIngredientPrice(name: string): IngredientPrice {
  const now = new Date().toISOString();
  return {
    id: '',
    name: name.toLowerCase(),
    displayName: name,
    price: 0,
    quantity: 1,
    unit: 'whole',
    costPerBaseUnit: 0,
    baseUnit: 'whole',
    category: 'other',
    source: 'manual',
    recordedAt: now,
    updatedAt: now,
    schemaVersion: PRICE_SCHEMA_VERSION,
  };
}
